// Datum zum Eintippen UND zum Anklicken (19.10.2026, Dilsar): EINE Komponente für jedes Datumsfeld im CRM, Gegenstück zu ZeitFeld.
// Tippen: „20.10“, „2010“, „20.10.26“, „morgen“, „übermorgen“, „mo“ … „so“, „+3“ (Erkennung: werkzeuge/datumLesen.ts).
// Klick ins Feld klappt einen Kalender auf (Monat blättern, Heute), Klick auf einen Tag übernimmt ihn. Außen sprechen die Felder wie
// ein <input type="date">: `value` ist YYYY-MM-DD oder "", `onChange` bekommt ein Ereignis mit `target.value` (ISO oder "").
// Ungültiges bleibt rot stehen und wird nie übernommen (der Wert davor gilt weiter). Eindeutige Eingaben („morgen“, „20.10.26“)
// gehen sofort nach außen, Teilstücke („2“, „20.1“) bei Enter oder beim Verlassen.
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ChangeEvent, type InputHTMLAttributes, type KeyboardEvent } from "react";
import { MONATE, WOCHENTAGE_KURZ, datumAnzeige, datumLesen, monatVerschieben, monatsRaster } from "../werkzeuge/datumLesen";
import { heute as heuteBerlin } from "../werkzeuge/zeit";
import { Ausklapp } from "./Ausklapp";

type Basis = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange" | "size">;
interface Props extends Basis {
  value: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Eingaben ohne Jahr gelten für den nächsten solchen Tag (Follow-ups, Planen); Standard: laufendes Jahr */
  zukunft?: boolean;
}

function ereignis(wert: string): ChangeEvent<HTMLInputElement> {
  return { target: { value: wert }, currentTarget: { value: wert } } as unknown as ChangeEvent<HTMLInputElement>;
}

export const DatumFeld = forwardRef<HTMLInputElement, Props>(function DatumFeld(
  { value, onChange, zukunft, min, max, className = "eingabe", disabled, onKeyDown, onFocus, onBlur, onClick: _onClick, required, placeholder, title, ...rest },
  ref,
) {
  const wert = (value ?? "").slice(0, 10);
  const [text, setText] = useState(datumAnzeige(wert));
  const [fokus, setFokus] = useState(false);
  const [offen, setOffen] = useState(false);
  const [versucht, setVersucht] = useState(false);
  const [anker, setAnker] = useState<HTMLInputElement | null>(null);
  const zuletzt = useRef(wert);
  useImperativeHandle(ref, () => anker as HTMLInputElement, [anker]);
  const heute = heuteBerlin();
  const start = wert || heute;
  const [ansicht, setAnsicht] = useState(() => ({ jahr: +start.slice(0, 4), monat: +start.slice(5, 7) }));

  useEffect(() => {
    zuletzt.current = wert;
    if (!fokus) setText(datumAnzeige(wert));
    if (wert) setAnsicht({ jahr: +wert.slice(0, 4), monat: +wert.slice(5, 7) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wert]);

  const bewertet = (t: string) => {
    if (!t.trim()) return { ok: !required, iso: "", vollstaendig: true };
    const r = datumLesen(t, heute, { zukunft: zukunft ?? !!min });
    const ausserhalb = !!r.iso && ((!!min && r.iso < min) || (!!max && r.iso > max));
    return { ok: !!r.iso && !ausserhalb, iso: r.iso ?? "", vollstaendig: r.vollstaendig };
  };
  const b = bewertet(text);
  const rot = !b.ok && (!fokus || versucht) && text.trim() !== "";

  const geben = (iso: string) => {
    if (iso === zuletzt.current) return;
    zuletzt.current = iso;
    onChange?.(ereignis(iso));
  };
  const uebernehmen = (iso: string) => {
    setText(datumAnzeige(iso));
    setVersucht(false);
    geben(iso);
  };

  const raster = monatsRaster(ansicht.jahr, ansicht.monat);
  const erlaubt = (d: string) => !((min && d < min) || (max && d > max));

  return (
    <>
      <input
        {...rest}
        ref={setAnker}
        type="text"
        inputMode="text"
        autoComplete="off"
        className={`${className}${rot ? " eingabe--fehler datumfeld--ungueltig" : ""}`}
        placeholder={placeholder ?? "TT.MM.JJJJ"}
        size={10}
        value={text}
        disabled={disabled}
        role="combobox"
        aria-expanded={offen}
        aria-haspopup="dialog"
        aria-invalid={rot}
        required={required}
        title={rot ? "Datum nicht erkannt oder nicht erlaubt" : (title ?? "Datum eintippen (z. B. 20.10, morgen, mo, +3) oder im Kalender wählen")}
        onFocus={(e) => {
          setFokus(true);
          e.currentTarget.select();
          setOffen(true);
          onFocus?.(e);
        }}
        onClick={() => setOffen(true)}
        onBlur={(e) => {
          setFokus(false);
          setVersucht(false);
          if (b.ok) {
            setText(datumAnzeige(b.iso));
            geben(b.iso);
          }
          onBlur?.(e);
        }}
        onChange={(e) => {
          const neu = e.target.value;
          setText(neu);
          setVersucht(false);
          setOffen(true);
          const x = bewertet(neu);
          if (x.ok && x.iso) setAnsicht({ jahr: +x.iso.slice(0, 4), monat: +x.iso.slice(5, 7) });
          if (x.ok && x.vollstaendig) geben(x.iso);
        }}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Escape" && offen) {
            setOffen(false);
            return;
          }
          if (e.key === "Enter") {
            if (!b.ok && text.trim() !== "") {
              e.preventDefault();
              setVersucht(true);
              return;
            }
            const geaendert = b.iso !== zuletzt.current;
            setText(datumAnzeige(b.iso));
            geben(b.iso);
            setOffen(false);
            // Enter hat nur übernommen, was noch offen war; das nächste Enter löst das Formular aus
            if (geaendert && !b.vollstaendig) {
              e.preventDefault();
              return;
            }
          }
          onKeyDown?.(e);
        }}
      />
      <Ausklapp anker={anker} offen={offen && !disabled} onSchliessen={() => setOffen(false)} breite={248} minBreite={248} maxHoehe={340} rolle="dialog" className="datumkalender-ausklapp ausklapp--ueber-dialog">
        {/* der Fokus bleibt im Feld: ein Klick in den Kalender darf es nicht verlassen */}
        <div className="datumkalender" role="presentation" onMouseDown={(e) => e.preventDefault()}>
          <div className="datumkalender__kopf">
            <button type="button" tabIndex={-1} className="symbolknopf symbolknopf--klein" aria-label="Vorheriger Monat" onClick={() => setAnsicht(monatVerschieben(ansicht.jahr, ansicht.monat, -1))}>
              ‹
            </button>
            <span className="datumkalender__monat">
              {MONATE[ansicht.monat - 1]} {ansicht.jahr}
            </span>
            <button type="button" tabIndex={-1} className="symbolknopf symbolknopf--klein" aria-label="Nächster Monat" onClick={() => setAnsicht(monatVerschieben(ansicht.jahr, ansicht.monat, 1))}>
              ›
            </button>
          </div>
          <div className="datumkalender__raster" role="grid">
            {WOCHENTAGE_KURZ.map((w) => (
              <span key={w} className="datumkalender__wt" role="columnheader">
                {w}
              </span>
            ))}
            {raster.flat().map((d, i) =>
              d ? (
                <button
                  key={d}
                  type="button"
                  tabIndex={-1}
                  data-tag={d}
                  disabled={!erlaubt(d)}
                  className={`datumkalender__tag${d === wert ? " datumkalender__tag--gewaehlt" : ""}${d === heute ? " datumkalender__tag--heute" : ""}`}
                  onClick={() => {
                    uebernehmen(d);
                    setOffen(false);
                  }}
                >
                  {+d.slice(8)}
                </button>
              ) : (
                <span key={`l${i}`} />
              ),
            )}
          </div>
          <div className="datumkalender__fuss">
            <button type="button" tabIndex={-1} className="textknopf" disabled={!erlaubt(heute)} onClick={() => { uebernehmen(heute); setOffen(false); }}>
              Heute
            </button>
            {!required ? (
              <button type="button" tabIndex={-1} className="textknopf" onClick={() => { uebernehmen(""); setOffen(false); }}>
                Leeren
              </button>
            ) : null}
          </div>
        </div>
      </Ausklapp>
    </>
  );
});
